import {
  AssetFilterModel,
  useAssetListFilter,
  useCurrentAccount,
  useCurrentWorkspace,
  useCustomQuery,
  useSetAssetListFilter,
  useValidateAssetListOrder,
  WorkspaceType,
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { array, string, object as yupObject } from 'yup';
import { FilterScreenLayout } from './filterScreenLayout';
import { ASSET_SECTIONS } from './filterSectionTypes';

const formSchema = yupObject({
  keyword: string().optional().nullable(),
  columns: array().of(string().optional().nullable()).optional().nullable(),
  groups: array().of(string().optional().nullable()).optional().nullable(),
});

export type AssetFilterScreenProps = {
  mode: 'page' | 'modal';
  onBack?: () => void;
  onDismiss?: () => void;
  isVisible?: boolean;
};

export function AssetFilterScreen({
  mode,
  onBack,
  onDismiss,
  isVisible,
}: AssetFilterScreenProps) {
  const { data: filter } = useAssetListFilter();
  const { mutateAsync: setAssetListFilter } = useSetAssetListFilter();
  const validateAssetListOrder = useValidateAssetListOrder();
  const currentWorkspace = useCurrentWorkspace();
  const { account } = useCurrentAccount();
  const isGlobalAdmin = account?.isGlobalAdmin?.() || false;
  const workspaceId = isGlobalAdmin ? undefined : (currentWorkspace?.id || account?.workspace?.id);

  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    values: {
      keyword: filter?.keyword || '',
      columns: (() => {
        if (!filter?.columns) return ['name'];
        const cols = Array.from(
          new Set((filter.columns as any[]).filter((c: any): c is string => c != null && typeof c === 'string'))
        );
        return cols.includes('name') ? cols : ['name', ...cols];
      })(),
      groups: filter?.groups ?? [],
    },
  });

  const { control, reset, setValue, watch } = formMethods;
  const formData = watch();

  const buildServerFilter = () => {
    const payload: any = { ...formData };
    if (!payload.keyword?.length) delete payload.keyword;
    if (payload.columns) {
      let columns = Array.from(
        new Set((payload.columns as any[]).filter((c: any): c is string => c != null && typeof c === 'string'))
      );
      if (!columns.includes('name')) columns = ['name', ...columns];
      const isOEM = currentWorkspace?.type === WorkspaceType.Oem;
      if (!isOEM) columns = columns.filter((col: string) => col !== 'productName');
      payload.columns = columns;
    } else {
      payload.columns = ['name'];
    }
    const assetFilterModel = new AssetFilterModel(payload);
    const filterOut: any = {
      columns: assetFilterModel.columns || ['name'],
      workspaceId: isGlobalAdmin ? undefined : workspaceId,
    };
    if (assetFilterModel.keyword) filterOut.keyword = assetFilterModel.keyword;
    const groupsArray = payload.groups || [];
    const validGroups = groupsArray.filter((g: string | null | undefined): g is string => g != null && g !== '');
    if (validGroups.length > 0) {
      filterOut.groups = validGroups;
      filterOut.isCombinedGroupsFilter = payload.isCombinedGroupsFilter ?? false;
    }
    return filterOut;
  };

  const serverFilterForCount = buildServerFilter();
  const { data: countData } = useCustomQuery({
    cacheKey: ['assets', 'filterCount', isGlobalAdmin ? 'global' : workspaceId || 'none', JSON.stringify(serverFilterForCount)],
    query: `
      query ListAssetsCount($filter: ListAssetsFilter!, $page: PageRequest) {
        asset {
          listAssets(filter: $filter, page: $page) {
            count
          }
        }
      }
    `,
    variables: { filter: serverFilterForCount, page: { limit: 1, offset: 0 } },
    options: { enabled: true },
  });
  const resultsCount = countData?.asset?.listAssets?.count ?? 0;

  const resetForm = useCallback(() => {
    const isOEM = currentWorkspace?.type === WorkspaceType.Oem;
    let defaultColumns = new AssetFilterModel().columns;
    if (defaultColumns) {
      if (isOEM) {
        const descriptionIndex = defaultColumns.indexOf('description');
        if (descriptionIndex !== -1 && !defaultColumns.includes('productName')) {
          defaultColumns = [
            ...defaultColumns.slice(0, descriptionIndex + 1),
            'productName',
            ...defaultColumns.slice(descriptionIndex + 1),
          ];
        }
      } else {
        defaultColumns = defaultColumns.filter((col) => col !== 'productName');
      }
    }
    reset({
      columns: defaultColumns && defaultColumns.includes('name') ? defaultColumns : ['name', ...(defaultColumns || [])],
      groups: [],
      keyword: '',
    });
  }, [reset, currentWorkspace]);

  useEffect(() => {
    if (!filter) {
      resetForm();
    } else {
      const isOEM = currentWorkspace?.type === WorkspaceType.Oem;
      if (!isOEM && filter.columns?.includes('productName')) {
        const updatedColumns = filter.columns.filter((col: string) => col !== 'productName');
        setValue('columns', updatedColumns, { shouldDirty: false });
      }
    }
  }, [filter, resetForm, currentWorkspace, setValue]);

  const onCloseAfterApply = mode === 'page' ? onBack : onDismiss;

  const onSubmit = async (data: any) => {
    if (!data?.keyword?.length) delete data.keyword;
    const isOEM = currentWorkspace?.type === WorkspaceType.Oem;
    if (data.columns) {
      let columns = Array.from(
        new Set((data.columns as any[]).filter((c: any): c is string => c != null && typeof c === 'string'))
      );
      if (!columns.includes('name')) columns = ['name', ...columns];
      if (!isOEM) columns = columns.filter((col: string) => col !== 'productName');
      data.columns = columns;
    }
    const filterModel = new AssetFilterModel(data);
    await Promise.all([setAssetListFilter(filterModel), validateAssetListOrder(filterModel)]);
    onCloseAfterApply?.();
  };

  const onReset = async () => {
    resetForm();
    const defaultFilter = new AssetFilterModel();
    await Promise.all([
      setAssetListFilter(defaultFilter),
      validateAssetListOrder(defaultFilter),
    ]);
  };

  const canReset = (): boolean => {
    const defaultFilter = new AssetFilterModel();
    return Boolean(
      (formData.keyword && formData.keyword.length > 0) ||
      JSON.stringify(formData.columns?.sort()) !== JSON.stringify(defaultFilter.columns?.sort()) ||
      (formData.groups && formData.groups.length > 0)
    );
  };

  const isOEM = currentWorkspace?.type === WorkspaceType.Oem;

  return (
    <FilterScreenLayout
      mode={mode}
      onBack={onBack}
      onDismiss={onDismiss}
      isVisible={isVisible}
      titleKey="assets.filter.title"
      formMethods={formMethods}
      resultsCount={resultsCount}
      viewResultsTranslationKey="assets.filter.viewResults"
      onReset={onReset}
      canReset={canReset}
      onSubmit={onSubmit}
      variant="asset"
      sections={ASSET_SECTIONS}
      control={control}
      workspaceId={workspaceId}
      isGlobalAdmin={isGlobalAdmin}
      isOEM={isOEM}
    />
  );
}
